import React, { ReactElement, useMemo, useState } from "react"
import { GatsbyImage } from "gatsby-plugin-image"
import { useApolloClient } from "@apollo/client/react"
import gql from "graphql-tag"
import { useLocation } from "@reach/router"
import getGatsbyImage from "utils/getGatsbyImage"
import { Dictionary, HomeProgram, HomeschoolProgram } from 'nzk-react-components'
import useHubspotTracking from "../../hooks/useHubspotTracking"
import * as s from "./index.styles"

interface IProps {
  bundle2Image: any
  logoImage: any
  onComplete: () => void
}

const USER_TYPE_OPTIONS: Array<{
  value: "Parent" | "Teacher" | "Homeschooler"
  label: string
  icon: ReactElement
}> = [
    { value: "Parent", label: "Parent", icon: <HomeProgram /> },
    { value: "Teacher", label: "Teacher", icon: <Dictionary /> },
    { value: "Homeschooler", label: "Homeschooler", icon: <HomeschoolProgram /> },
  ]

const FormComponent = (props: IProps) => {
  const { bundle2Image, logoImage, onComplete } = props

  const [userType, setUserType] = useState<
    "Teacher" | "Parent" | "Homeschooler"
  >("Parent")
  const location = useLocation()
  const { identify, trackEvent } = useHubspotTracking()
  const [formData, setFormData] = useState<any>({
    firstName: "",
    email: "",
  })
  const client = useApolloClient()

  const canSubmit = useMemo(() => {
    const emailRe =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return formData.firstName !== "" && emailRe.test(formData.email.toLowerCase())
  }, [formData])

  const submit = async () => {
    if (!canSubmit) return
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    if (!re.test(formData.email.toLowerCase())) return
    await client.mutate({
      mutation: gql`
        mutation ($name: String, $email: String!) {
          submitExitIntentPopup(name: $name, email: $email)
        }
      `,
      variables: {
        email: formData.email.toLowerCase(),
        name: formData.firstName,
      },
    })
    let contact_type = "Parent"
    let homeschooler = "false"

    if (userType === "Homeschooler") {
      homeschooler = "true"
    } else if (userType === "Teacher") {
      contact_type = "Teacher"
    }

    identify({
      email: formData.email.toLowerCase(),
      firstname: formData.firstName,
      contact_type,
      homeschooler,
    })
    trackEvent({ id: "Exit Intent Form Submitted" })
    onComplete()
    try {
      if (typeof window !== "undefined") {
        window.postMessage(
          {
            event: "ExitIntentSubmitted",
            payload: {
              email: formData.email.toLowerCase(),
              firstName: formData.firstName,
            },
          },
          location.origin
        )
      }
    } catch (err) {
      console.error(err)
    }
  }

  return (
    <>
      <div style={{ position: "absolute", top: "12px", left: "12px", width: "60px", zIndex: 1 }}>
        <GatsbyImage
          image={getGatsbyImage(logoImage)}
          imgStyle={{ objectFit: "contain" }}
          alt="Logo"
        />
      </div>
      <s.StyledHeading style={{
        fontSize: '1.2em'
      }}>Before you go...</s.StyledHeading>
      <GatsbyImage
        image={getGatsbyImage(bundle2Image)}
        style={{ margin: "10px 0", width: "260px" }}
        imgStyle={{ objectFit: "contain" }}
        alt="Free Resources"
      />
      <s.Subtitle>Grab a <strong>free resource pack</strong><br /> for your young writer!</s.Subtitle>
      <s.FormWrapper>
        <s.ScrollWrapper>
          <s.Form>
            <s.ModernInput
              placeholder="First name"
              value={formData.firstName}
              onChange={e =>
                setFormData(f => ({ ...f, firstName: e.target.value }))
              }
            />
            <s.ModernInput
              type="email"
              placeholder="Email address"
              value={formData.email}
              onChange={e =>
                setFormData(f => ({ ...f, email: e.target.value }))
              }
            />
          </s.Form>
          <s.UserTypeSelector>
            <div className="title">I am a:</div>
            <s.UserTypeCards>
              {USER_TYPE_OPTIONS.map(option => (
                <s.UserTypeCard
                  key={option.value}
                  selected={userType === option.value}
                  onClick={() => {
                    setUserType(option.value)
                    setFormData(f => ({ ...f, userType: option.value }))
                  }}
                >
                  <span className="icon">{option.icon}</span>
                  <span className="label">{option.label}</span>
                </s.UserTypeCard>
              ))}
            </s.UserTypeCards>
          </s.UserTypeSelector>
          <s.Permission>
            By entering your email, you give Night Zookeeper permission to
            contact you about their product and services.
          </s.Permission>
        </s.ScrollWrapper>
      </s.FormWrapper>
      <s.SubmitButton disabled={!canSubmit} onClick={submit}>
        Get My Free Resources
      </s.SubmitButton>
    </>
  )
}

export default FormComponent
